import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { RESOURCES } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPENING_LINE = "What are you noticing right now?";

const getSessionId = () => {
  let id = sessionStorage.getItem("companion-session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("companion-session", id);
  }
  return id;
};

// Extracts an optional [[resource:slug]] marker; hides partial markers mid-stream.
const parseReply = (raw, streaming) => {
  let t = raw;
  const open = t.lastIndexOf("[[");
  if (streaming && open !== -1 && t.indexOf("]]", open) === -1) t = t.slice(0, open);
  if (streaming && t.endsWith("[")) t = t.slice(0, -1);
  const m = t.match(/\[\[resource:([a-z-]+)\]\]/);
  return { text: t.replace(/\[\[resource:[a-z-]+\]\]/g, "").trim(), slug: m ? m[1] : null };
};

const ResourceLink = ({ slug }) => {
  const resource = RESOURCES.find((r) => r.slug === slug);
  if (!resource) return null;
  return (
    <button
      type="button"
      data-testid="companion-resource-link"
      onClick={() => document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" })}
      className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-teal hover:text-deepteal transition-colors duration-500 border-b border-teal/40 pb-1"
    >
      {resource.need}
      <ArrowRight size={13} />
    </button>
  );
};

const CompanionPanel = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (messages.length > 0) endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/companion/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.error) errored = true;
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + data.delta };
              return copy;
            });
          }
        }
      }
      if (errored) throw new Error("stream error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (!last.text) {
          copy[copy.length - 1] = {
            ...last,
            text: "The companion is resting for a moment. Please try again shortly.",
          };
        }
        return copy;
      });
    }
    setStreaming(false);
    inputRef.current?.focus();
  };

  const beginAgain = () => {
    if (streaming) return;
    sessionStorage.removeItem("companion-session");
    setMessages([]);
    setInput("");
    inputRef.current?.focus();
  };

  return (
    <div
      data-testid="companion-panel"
      className="border border-stone bg-white/60 p-8 md:p-12 relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-[260px] aspect-square rounded-full bg-[radial-gradient(circle,rgba(76,144,128,0.06),transparent_65%)] blur-2xl"
      />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.3em] text-teal mb-4">Not sure what you need?</p>
        <h2 className="font-serif text-2xl md:text-4xl text-charcoal leading-snug">Start where you are.</h2>

        <AnimatePresence mode="wait">
          {!open ? (
            <motion.div key="closed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
              <p className="mt-5 text-base leading-relaxed text-taupe max-w-xl">
                A quiet space to check in with yourself. Share what&rsquo;s present, and
                we&rsquo;ll find a gentle place to begin.
              </p>
              <button
                type="button"
                data-testid="companion-begin-button"
                onClick={() => setOpen(true)}
                className="mt-8 inline-flex items-center gap-2 border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-8 py-3.5 hover:border-teal hover:text-deepteal transition-colors duration-500"
              >
                Begin
              </button>
            </motion.div>
          ) : (
            <motion.div key="open" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
              <div className="mt-8 space-y-8 max-w-xl" data-testid="companion-thread">
                <p className="font-serif italic text-xl md:text-2xl text-deepteal leading-relaxed">
                  {OPENING_LINE}
                </p>

                {messages.map((m, i) => {
                  if (m.role === "user") {
                    return (
                      <p key={i} data-testid={`companion-message-${i}`} className="text-base leading-relaxed text-charcoal pl-5 border-l border-stone/70">
                        {m.text}
                      </p>
                    );
                  }
                  const isLast = i === messages.length - 1;
                  const { text, slug } = parseReply(m.text, streaming && isLast);
                  return (
                    <div key={i} data-testid={`companion-message-${i}`}>
                      {text && (
                        <p className="font-serif italic text-lg md:text-xl text-deepteal leading-relaxed whitespace-pre-line">
                          {text}
                        </p>
                      )}
                      {slug && (!streaming || !isLast) && <ResourceLink slug={slug} />}
                      {streaming && isLast && (
                        <span
                          data-testid="companion-breathing-dot"
                          className="anim-breathe mt-3 inline-block h-2.5 w-2.5 rounded-full bg-teal"
                        />
                      )}
                    </div>
                  );
                })}
                <div ref={endRef} />

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                  className="flex items-end gap-4 pt-2"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Write what's here…"
                    maxLength={2000}
                    data-testid="companion-input"
                    className="flex-1 bg-transparent border-0 border-b border-stone focus:border-teal outline-none focus:outline-none focus:ring-0 py-2 text-base text-charcoal placeholder:text-taupe/60 transition-colors duration-500"
                  />
                  <button
                    type="submit"
                    disabled={streaming || !input.trim()}
                    data-testid="companion-send-button"
                    aria-label="Share"
                    className="text-xs uppercase tracking-[0.25em] text-charcoal hover:text-deepteal transition-colors duration-500 pb-2 disabled:opacity-40"
                  >
                    Share
                  </button>
                </form>

                <div className="flex items-start justify-between gap-6">
                  <p className="text-xs leading-relaxed text-taupe/70 max-w-md">
                    A quiet companion to REGULATE — not therapy or medical advice. If you
                    are in crisis, please reach your local emergency services, or in the
                    US call or text 988.
                  </p>
                  {messages.length > 0 && !streaming && (
                    <button
                      type="button"
                      data-testid="companion-begin-again-button"
                      onClick={beginAgain}
                      className="shrink-0 text-[11px] uppercase tracking-[0.25em] text-taupe/70 hover:text-deepteal border-b border-transparent hover:border-teal/40 pb-0.5 transition-colors duration-500"
                    >
                      Begin again
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CompanionPanel;
